import { forwardRef } from 'preact/compat';
import { useCallback, useEffect } from 'preact/hooks';

import { useMenuSwipe } from './useMenuSwipe';
import useModal from '../UNSAFE_Modal/hooks/useModal';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { Layer } from '../UNSAFE_Layer';
import { classNames } from '../utils/UNSAFE_classNames';

import { BaseMenuProps } from './Menu';
import { useMenuContext } from './useMenuContext';
import { MenuContainer } from './MenuContainer';
import { sheetMenuStyles } from './themes/SheetMenuStyles.css';

type IntrinsicProps = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'children'>;

export type SheetMenuProps = BaseMenuProps;

const ModalWrapper = ({ children }: IntrinsicProps) => (
  <div class={sheetMenuStyles.modalWrapper}>{children}</div>
);
const ModalContainer = forwardRef<HTMLDivElement, IntrinsicProps>(({ children }, ref) => (
  <div class={sheetMenuStyles.modalContainer} ref={ref}>
    {children}
  </div>
));
//ModalBackdrop on modal folder not setting the style on oj-c enviroment
//Creating it here so we don't see that issue
const ModalBackdrop = ({ backdropVisible = false }) => (
  <div
    class={classNames([
      sheetMenuStyles.modalBackdrop,
      backdropVisible && sheetMenuStyles.modalBackdropVisible
    ])}
  />
);

export const SheetMenu = ({
  children,
  'aria-label': ariaLabel,
  currentFocus,
  anchorRef,
  closeSubmenu,
  menuLevel
}: SheetMenuProps) => {
  const { onClose } = useMenuContext();
  useEffect(() => {
    // We set the overflow body as hidden when mounted and revert to original overflow value when is
    // unmounted. This is done to don't allow backgound being scrolled
    // This will be handled by modal/popup when ready
    const initialOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = initialOverflow;
    };
  }, []);

  const dismissHandler = useCallback(() => {
    onClose?.({ reason: 'dismissed' });
  }, [onClose]);

  const { backdropRef } = useModal({ isOpen: true, onBackdropClick: dismissHandler });
  //TODO: Use useSwipe when it works on components that could scroll.
  const { swipeProps } = useMenuSwipe(dismissHandler);

  // TODO: For now we are mimicking current modal but using a "different position setting" (using absolute positioning instead of flex).
  // Have to use modal/popup component directly instead doing this composition when that component could satisfy our requirments.
  return (
    <Layer logicalParentRef={anchorRef}>
      <ModalWrapper>
        <ModalBackdrop backdropVisible={menuLevel === 'main'}></ModalBackdrop>
        <ModalContainer ref={backdropRef}>
          <div class={sheetMenuStyles.sheetMenuContainerWrapper} {...swipeProps}>
            <MenuContainer
              {...{ ariaLabel, currentFocus, display: 'sheet', closeSubmenu, menuLevel }}>
              {children}
            </MenuContainer>
          </div>
        </ModalContainer>
      </ModalWrapper>
    </Layer>
  );
};
